import { useState, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Upload, FileSpreadsheet, ArrowLeft, ArrowRight, Download, CheckCircle2,
  AlertTriangle, XCircle, Loader2, MapPin, Eye, Zap, ChevronDown, ChevronUp,
  FileWarning, Shield, Clock, Hash
} from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Upload", "Preview", "Map Fields", "Audit & Import"];

function StepIndicator({ current }) {
  return (
    <div className="flex items-center gap-1" data-testid="step-indicator">
      {STEPS.map((label, i) => (
        <div key={label} className="flex items-center">
          <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium transition-all ${
            i < current ? "bg-[#10b981]/10 text-[#10b981]" :
            i === current ? "bg-[#4338ca] text-white" :
            "bg-[#f1f5f9] text-[#94a3b8]"
          }`} data-testid={`step-${i}`}>
            {i < current ? <CheckCircle2 className="h-3 w-3" /> : <span className="w-4 text-center">{i + 1}</span>}
            <span className="hidden sm:inline">{label}</span>
          </div>
          {i < STEPS.length - 1 && <div className={`w-6 h-px mx-1 ${i < current ? "bg-[#10b981]" : "bg-[#e2e8f0]"}`} />}
        </div>
      ))}
    </div>
  );
}

// ===== STEP 1: Upload =====
function UploadStep({ onParsed }) {
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);

  const downloadTemplate = async () => {
    try {
      const response = await api.get("/import/sample-template", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const a = document.createElement("a"); a.href = url; a.download = "bizflowa_import_template.csv";
      document.body.appendChild(a); a.click(); a.remove(); window.URL.revokeObjectURL(url);
      toast.success("Template downloaded!");
    } catch { toast.error("Failed to download template"); }
  };

  const processFile = async (file) => {
    if (!file || !file.name.endsWith(".csv")) {
      toast.error("Please upload a .csv file");
      return;
    }
    setLoading(true);
    try {
      const text = await file.text();
      const response = await api.post("/import/parse-csv", { csv_content: text });
      onParsed({ ...response.data, csv_content: text });
      toast.success(`Parsed ${response.data.total_rows} rows`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to parse CSV");
    } finally {
      setLoading(false);
    }
  };

  const onDrop = useCallback((e) => {
    e.preventDefault(); setDragging(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) processFile(file);
  }, []);

  const onFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
    e.target.value = "";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#0f172a]">Upload Sales Data</h2>
          <p className="text-sm text-[#64748b] mt-0.5">Upload a CSV file with your sales/invoice data</p>
        </div>
        <Button variant="outline" size="sm" onClick={downloadTemplate} data-testid="download-template-btn"
          className="border-[#4338ca]/20 text-[#4338ca] hover:bg-[#4338ca]/5 rounded-lg">
          <Download className="h-3.5 w-3.5 mr-1.5" /> Download Sample Template
        </Button>
      </div>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        data-testid="csv-drop-zone"
        className={`relative border-2 border-dashed rounded-2xl p-12 text-center transition-all cursor-pointer
          ${dragging ? "border-[#4338ca] bg-[#4338ca]/5" : "border-[#e2e8f0] hover:border-[#cbd5e1] bg-[#f8fafc]"}`}
      >
        <input type="file" accept=".csv" onChange={onFileSelect} className="absolute inset-0 opacity-0 cursor-pointer" data-testid="csv-file-input" />
        {loading ? (
          <div className="flex flex-col items-center gap-3">
            <Loader2 className="h-10 w-10 text-[#4338ca] animate-spin" />
            <p className="text-sm text-[#64748b]">Reading CSV file...</p>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-3">
            <div className="h-14 w-14 rounded-2xl bg-[#4338ca]/10 flex items-center justify-center">
              <Upload className="h-6 w-6 text-[#4338ca]" />
            </div>
            <div>
              <p className="text-sm font-medium text-[#0f172a]">Drop your CSV file here, or click to browse</p>
              <p className="text-xs text-[#94a3b8] mt-1">Supports .csv files with sales invoice data</p>
            </div>
          </div>
        )}
      </div>

      <div className="bg-[#f8fafc] rounded-xl border border-[#e2e8f0] p-4">
        <p className="text-xs font-semibold text-[#64748b] uppercase tracking-wider mb-2">Expected columns</p>
        <div className="flex flex-wrap gap-1.5">
          {["Invoice No", "Date", "Customer Name", "GSTIN", "HSN", "Description", "Qty", "Rate", "Taxable Value", "CGST", "SGST", "IGST", "Place of Supply"].map((col) => (
            <span key={col} className="px-2 py-1 bg-white border border-[#e2e8f0] rounded-md text-[11px] text-[#64748b] font-mono">{col}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ===== STEP 2: Preview =====
function PreviewStep({ data }) {
  const { headers, preview, total_rows } = data;
  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-[#0f172a]">Data Preview</h2>
          <p className="text-sm text-[#64748b]">Showing first {preview.length} of {total_rows} rows</p>
        </div>
        <Badge className="bg-[#4338ca]/10 text-[#4338ca]">
          <FileSpreadsheet className="h-3 w-3 mr-1" /> {total_rows} rows detected
        </Badge>
      </div>
      <div className="overflow-x-auto border border-[#e2e8f0] rounded-xl">
        <table className="w-full text-xs" data-testid="preview-table">
          <thead>
            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-10">#</th>
              {headers.map((h, i) => (
                <th key={i} className="py-2.5 px-3 text-left text-[#64748b] font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {preview.map((row, rIdx) => (
              <tr key={rIdx} className="border-b border-[#f1f5f9] last:border-0" data-testid={`preview-row-${rIdx}`}>
                <td className="py-2 px-3 text-[#94a3b8] font-mono">{rIdx + 1}</td>
                {row.map((cell, cIdx) => (
                  <td key={cIdx} className="py-2 px-3 text-[#0f172a] whitespace-nowrap max-w-[200px] truncate">{cell || <span className="text-[#cbd5e1]">—</span>}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ===== STEP 3: Mapping =====
function MappingStep({ data, mapping, setMapping }) {
  const { headers, suggested_mapping, bizflowa_fields } = data;
  const unmappedCsvHeaders = headers.filter((h) => !Object.values(mapping).includes(h));

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold text-[#0f172a]">Map Your Columns</h2>
        <p className="text-sm text-[#64748b]">Match your CSV columns to BizFlowa fields. Smart suggestions are pre-filled.</p>
      </div>

      <div className="border border-[#e2e8f0] rounded-xl overflow-hidden" data-testid="mapping-table">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <th className="py-3 px-4 text-left text-[#64748b] font-medium w-1/2">BizFlowa Field</th>
              <th className="py-3 px-4 text-left text-[#64748b] font-medium w-1/2">Your CSV Column</th>
            </tr>
          </thead>
          <tbody>
            {bizflowa_fields.map((bf) => {
              const isMatched = !!mapping[bf];
              return (
                <tr key={bf} className="border-b border-[#f1f5f9] last:border-0">
                  <td className="py-2.5 px-4">
                    <div className="flex items-center gap-2">
                      {isMatched ? <CheckCircle2 className="h-3.5 w-3.5 text-[#10b981]" /> : <div className="h-3.5 w-3.5 rounded-full border-2 border-[#cbd5e1]" />}
                      <span className={`text-sm font-medium ${isMatched ? "text-[#0f172a]" : "text-[#94a3b8]"}`}>{bf}</span>
                      {["Customer Name", "Taxable Value", "HSN"].includes(bf) && <span className="text-[9px] text-red-400 font-bold">REQ</span>}
                    </div>
                  </td>
                  <td className="py-2.5 px-4">
                    <Select value={mapping[bf] || "_none"} onValueChange={(v) => {
                      const next = { ...mapping };
                      if (v === "_none") { delete next[bf]; } else { next[bf] = v; }
                      setMapping(next);
                    }}>
                      <SelectTrigger className="h-8 text-xs" data-testid={`mapping-select-${bf.replace(/\s/g, "-").toLowerCase()}`}>
                        <SelectValue placeholder="— Not mapped —" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="_none"><span className="text-[#94a3b8]">— Not mapped —</span></SelectItem>
                        {headers.map((h) => (
                          <SelectItem key={h} value={h}>{h}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {unmappedCsvHeaders.length > 0 && (
        <div className="text-xs text-[#94a3b8]">
          Unmapped CSV columns: {unmappedCsvHeaders.join(", ")}
        </div>
      )}
    </div>
  );
}

// ===== STEP 4: Audit Results =====
function AuditStep({ auditResult, onCommitValid, onRetry, committing }) {
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [showFilter, setShowFilter] = useState("all"); // all | errors | warnings | valid

  if (!auditResult) return null;

  const { total_rows, valid_count, invalid_count, error_summary, rows, grouped_invoices, group_summary } = auditResult;
  const hasGroups = grouped_invoices && grouped_invoices.length > 0;
  const multiItemCount = group_summary?.multi_item_invoices || 0;

  const toggleRow = (rowNum) => {
    const next = new Set(expandedRows);
    next.has(rowNum) ? next.delete(rowNum) : next.add(rowNum);
    setExpandedRows(next);
  };

  const filtered = rows.filter((r) => {
    if (showFilter === "errors") return !r.valid;
    if (showFilter === "warnings") return r.warnings.length > 0 && r.valid;
    if (showFilter === "valid") return r.valid && r.warnings.length === 0;
    return true;
  });

  const errorIcon = (type) => {
    switch (type) {
      case "hsn": return <Hash className="h-3 w-3" />;
      case "tax_logic": return <MapPin className="h-3 w-3" />;
      case "thirty_day": return <Clock className="h-3 w-3" />;
      case "gstin_invalid": return <Shield className="h-3 w-3" />;
      default: return <FileWarning className="h-3 w-3" />;
    }
  };

  return (
    <div className="space-y-5">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="audit-summary">
        <Card className="border-[#e2e8f0]">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#0f172a]">{total_rows}</p>
            <p className="text-[11px] text-[#64748b] mt-0.5">Total Rows</p>
          </CardContent>
        </Card>
        <Card className="border-[#10b981]/20 bg-[#10b981]/5">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#10b981]">{valid_count}</p>
            <p className="text-[11px] text-[#10b981] mt-0.5">Ready to Import</p>
          </CardContent>
        </Card>
        <Card className="border-red-200 bg-red-50">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-red-500">{invalid_count}</p>
            <p className="text-[11px] text-red-400 mt-0.5">Has Errors</p>
          </CardContent>
        </Card>
        <Card className="border-[#f59e0b]/20 bg-[#f59e0b]/5">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-bold text-[#f59e0b]">{rows.filter((r) => r.warnings.length > 0).length}</p>
            <p className="text-[11px] text-[#f59e0b] mt-0.5">Warnings</p>
          </CardContent>
        </Card>
      </div>

      {/* Multi-line Item Grouping Info */}
      {multiItemCount > 0 && (
        <div className="bg-[#4338ca]/5 border border-[#4338ca]/20 rounded-xl p-4 space-y-2" data-testid="grouping-info">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-4 w-4 text-[#4338ca]" />
            <p className="text-sm font-semibold text-[#4338ca]">Multi-Line Item Grouping Active</p>
          </div>
          <p className="text-xs text-[#64748b]">
            {valid_count} valid rows will create <span className="font-bold text-[#0f172a]">{group_summary.total_invoices} invoice{group_summary.total_invoices !== 1 ? "s" : ""}</span>
            {" "}({multiItemCount} with multiple line items, {group_summary.single_item_invoices} single-item)
          </p>
          {grouped_invoices.filter((g) => g.item_count > 1).map((g) => (
            <div key={g.invoice_no} className="flex items-center gap-2 text-xs text-[#64748b] pl-6">
              <span className="font-mono text-[#0f172a]">{g.invoice_no}</span>
              <span>&rarr;</span>
              <span>{g.header.customer_name}</span>
              <Badge className="bg-[#4338ca]/10 text-[#4338ca] text-[10px]">{g.item_count} items</Badge>
              <span className="ml-auto font-mono text-[#0f172a]">{formatINR(g.totals.taxable_value)}</span>
            </div>
          ))}
        </div>
      )}

      {/* Error Breakdown */}
      {invalid_count > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 space-y-2" data-testid="error-breakdown">
          <p className="text-sm font-semibold text-red-700">Issues Found</p>
          <div className="flex flex-wrap gap-2">
            {error_summary.hsn > 0 && (
              <Badge className="bg-red-100 text-red-600 gap-1"><Hash className="h-3 w-3" /> {error_summary.hsn} invalid HSN codes</Badge>
            )}
            {error_summary.gstin_invalid > 0 && (
              <Badge className="bg-red-100 text-red-600 gap-1"><Shield className="h-3 w-3" /> {error_summary.gstin_invalid} invalid GSTINs</Badge>
            )}
            {error_summary.tax_logic > 0 && (
              <Badge className="bg-red-100 text-red-600 gap-1"><MapPin className="h-3 w-3" /> {error_summary.tax_logic} tax logic errors</Badge>
            )}
            {error_summary.missing_fields > 0 && (
              <Badge className="bg-red-100 text-red-600 gap-1"><FileWarning className="h-3 w-3" /> {error_summary.missing_fields} missing fields</Badge>
            )}
            {error_summary.duplicate > 0 && (
              <Badge className="bg-red-100 text-red-600 gap-1"><Eye className="h-3 w-3" /> {error_summary.duplicate} duplicates already in BizFlowa</Badge>
            )}
          </div>
        </div>
      )}

      {/* Filter Tabs */}
      <div className="flex items-center gap-2">
        {[
          { key: "all", label: `All (${total_rows})` },
          { key: "errors", label: `Errors (${invalid_count})` },
          { key: "warnings", label: `Warnings (${rows.filter((r) => r.warnings.length > 0).length})` },
          { key: "valid", label: `Clean (${rows.filter((r) => r.valid && r.warnings.length === 0).length})` },
        ].map((tab) => (
          <button key={tab.key} onClick={() => setShowFilter(tab.key)} data-testid={`filter-${tab.key}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${
              showFilter === tab.key ? "bg-[#4338ca] text-white" : "bg-[#f1f5f9] text-[#64748b] hover:bg-[#e2e8f0]"
            }`}>{tab.label}</button>
        ))}
      </div>

      {/* Row-by-Row Results */}
      <div className="border border-[#e2e8f0] rounded-xl overflow-hidden max-h-[340px] overflow-y-auto" data-testid="audit-rows">
        <table className="w-full text-xs">
          <thead className="sticky top-0 z-10">
            <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
              <th className="py-2.5 px-3 text-left text-[#64748b] font-medium w-12">Row</th>
              <th className="py-2.5 px-3 text-left text-[#64748b] font-medium">Customer</th>
              <th className="py-2.5 px-3 text-left text-[#64748b] font-medium">Description</th>
              <th className="py-2.5 px-3 text-right text-[#64748b] font-medium">Amount</th>
              <th className="py-2.5 px-3 text-center text-[#64748b] font-medium w-20">Status</th>
              <th className="py-2.5 px-3 w-8" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((r) => {
              const expanded = expandedRows.has(r.row);
              const hasIssues = r.errors.length > 0 || r.warnings.length > 0;
              return (
                <tr key={r.row} className="border-b border-[#f1f5f9] last:border-0 group" data-testid={`audit-row-${r.row}`}>
                  <td className="py-2 px-3 font-mono text-[#94a3b8] align-top">{r.row}</td>
                  <td className="py-2 px-3 text-[#0f172a] font-medium align-top">{r.data.customer_name || "—"}</td>
                  <td className="py-2 px-3 text-[#64748b] align-top">{r.data.description || "—"}</td>
                  <td className="py-2 px-3 text-right font-mono text-[#0f172a] align-top">{formatINR(r.data.taxable_value)}</td>
                  <td className="py-2 px-3 text-center align-top">
                    {r.valid ? (
                      r.warnings.length > 0
                        ? <Badge className="bg-[#f59e0b]/10 text-[#f59e0b] text-[10px]">Warn</Badge>
                        : <Badge className="bg-[#10b981]/10 text-[#10b981] text-[10px]">OK</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-500 text-[10px]">Error</Badge>
                    )}
                  </td>
                  <td className="py-2 px-3 align-top">
                    {hasIssues && (
                      <button onClick={() => toggleRow(r.row)} className="text-[#94a3b8] hover:text-[#64748b]">
                        {expanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {/* Expanded error details rendered as separate rows for simplicity */}
            {filtered.map((r) => {
              if (!expandedRows.has(r.row)) return null;
              return (
                <tr key={`${r.row}-detail`} className="bg-[#fefce8]">
                  <td colSpan={6} className="px-6 py-2">
                    <div className="space-y-1">
                      {r.errors.map((e, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-red-600">
                          <XCircle className="h-3 w-3 shrink-0" /> {e.message}
                        </div>
                      ))}
                      {r.warnings.map((w, i) => (
                        <div key={i} className="flex items-center gap-2 text-xs text-[#f59e0b]">
                          <AlertTriangle className="h-3 w-3 shrink-0" /> {w.message}
                        </div>
                      ))}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-between pt-2" data-testid="audit-actions">
        <Button variant="outline" onClick={onRetry} className="rounded-xl" data-testid="fix-errors-retry-btn">
          <ArrowLeft className="h-4 w-4 mr-2" /> Fix Errors & Retry
        </Button>
        <Button onClick={onCommitValid} disabled={committing || valid_count === 0} data-testid="import-valid-btn"
          className="bg-[#10b981] hover:bg-[#059669] rounded-xl shadow-sm">
          {committing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
          {committing ? "Importing..." : hasGroups
            ? `Import ${group_summary.total_invoices} Invoice${group_summary.total_invoices !== 1 ? "s" : ""} (${valid_count} rows)`
            : `Import ${valid_count} Valid Row${valid_count !== 1 ? "s" : ""}`
          }
        </Button>
      </div>
    </div>
  );
}

// ===== MAIN PAGE =====
export default function SalesImporterPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [csvData, setCsvData] = useState(null);
  const [mapping, setMapping] = useState({});
  const [auditResult, setAuditResult] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [committing, setCommitting] = useState(false);

  const handleParsed = (data) => {
    setCsvData(data);
    setMapping(data.suggested_mapping || {});
    setStep(1);
  };

  const runAudit = async () => {
    if (!csvData) return;
    setAuditing(true);
    try {
      const response = await api.post("/import/audit", {
        csv_content: csvData.csv_content,
        mapping,
      });
      setAuditResult(response.data);
      setStep(3);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Audit failed");
    } finally {
      setAuditing(false);
    }
  };

  const commitValid = async () => {
    if (!auditResult) return;
    setCommitting(true);
    try {
      // Separate grouped vs ungrouped valid rows
      const groupedInvNos = new Set();
      const grouped = auditResult.grouped_invoices || [];
      grouped.forEach((g) => { if (g.invoice_no) groupedInvNos.add(g.invoice_no); });

      // Ungrouped = valid rows whose invoice_no is NOT in any group, or rows without invoice_no
      const ungroupedRows = auditResult.rows
        .filter((r) => r.valid && !groupedInvNos.has((r.data.invoice_no || "").trim()))
        .map((r) => r.data);

      const response = await api.post("/import/commit", {
        rows: ungroupedRows,
        grouped_invoices: grouped,
      });
      toast.success(`Imported ${response.data.imported_count} invoices!`);
      setTimeout(() => navigate("/invoices"), 1200);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  const handleRetry = () => {
    setAuditResult(null);
    setStep(0);
    setCsvData(null);
    setMapping({});
  };

  const canProceed = useMemo(() => {
    if (step === 1) return true;
    if (step === 2) return Object.keys(mapping).length >= 3; // At least Customer, Taxable, HSN
    return false;
  }, [step, mapping]);

  return (
    <div className="p-6 md:p-8 space-y-6 max-w-4xl mx-auto" data-testid="sales-importer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => navigate("/invoices")} className="text-[#64748b] hover:text-[#0f172a]" data-testid="back-to-invoices-btn">
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="font-outfit text-2xl font-bold text-[#0f172a] tracking-tight">Sales Data Importer</h1>
            <p className="text-sm text-[#64748b] mt-0.5">Import invoices from CSV with 2026 compliance checks</p>
          </div>
        </div>
      </div>

      {/* Step Indicator */}
      <StepIndicator current={step} />

      {/* Step Content */}
      <Card className="bg-white border-[#e2e8f0] shadow-sm">
        <CardContent className="p-6">
          {step === 0 && <UploadStep onParsed={handleParsed} />}
          {step === 1 && csvData && <PreviewStep data={csvData} />}
          {step === 2 && csvData && <MappingStep data={csvData} mapping={mapping} setMapping={setMapping} />}
          {step === 3 && <AuditStep auditResult={auditResult} onCommitValid={commitValid} onRetry={handleRetry} committing={committing} />}
        </CardContent>
      </Card>

      {/* Navigation (steps 1 & 2) */}
      {step > 0 && step < 3 && (
        <div className="flex items-center justify-between">
          <Button variant="outline" onClick={() => setStep(step - 1)} className="rounded-xl" data-testid="prev-step-btn">
            <ArrowLeft className="h-4 w-4 mr-2" /> Back
          </Button>
          {step === 2 ? (
            <Button onClick={runAudit} disabled={auditing || !canProceed} data-testid="map-and-scan-btn"
              className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
              {auditing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Zap className="h-4 w-4 mr-2" />}
              {auditing ? "Scanning..." : "Map & Scan"}
            </Button>
          ) : (
            <Button onClick={() => setStep(step + 1)} disabled={!canProceed} data-testid="next-step-btn"
              className="bg-[#4338ca] hover:bg-[#3730a3] rounded-xl shadow-sm">
              Next <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
}
